import React from 'react'
import BigNumber from 'bignumber.js'
import { Stack } from '@mantine/core'
import { useQuery } from 'react-query'
import { useWalletConnection } from '@/atoms/WalletConnection'
import { fatal } from '@/utils'
import { AIRDROP_CONFIG, STRIDE_CURRENCY } from '@/config'
import { useTaskStatusQuery, useTotalClaimableQuery, useRebateProgramQuery } from '../queries'
import { AirdropTaskItem } from './AirdropTaskItem'
import { AirdropTaskExternalLinkButton } from './AirdropTaskExternalLinkButton'
import { AirdropTaskClaimButton } from './AirdropTaskClaimButton'
import { AirdropTaskClaimRebateButton } from './AirdropTaskClaimRebateButton'
import { convertMicroDenomToDenom } from '@/atoms/Wallet'

type AirdropTaskKeys = keyof typeof AIRDROP_CONFIG.weights

const AirdropTasks: React.FC = () => {
  const { getWallet } = useWalletConnection()

  const { data: wallet, isLoading } = useQuery(['airdrop-wallet'], () => {
    return getWallet()
  })

  const { data: totalClaimable } = useTotalClaimableQuery()

  const { data: taskStatus } = useTaskStatusQuery()

  const { data: rebateProgram } = useRebateProgramQuery()

  if (totalClaimable == null || taskStatus == null || rebateProgram == null) {
    throw fatal('Total claimable data and claim record should have been fetched by the previous screen.')
  }

  const getValueFor = (key: AirdropTaskKeys) => {
    return convertMicroDenomToDenom(
      new BigNumber(totalClaimable.total).multipliedBy(AIRDROP_CONFIG.weights[key]).decimalPlaces(2).toString()
    )
  }

  const taskRewards = {
    claim: getValueFor('claim'),
    stake: getValueFor('stake'),
    liquidStake: getValueFor('liquidStake')
  }

  return (
    <>
      <Stack spacing="sm">
        {rebateProgram.status !== 'ineligible' && (
          <AirdropTaskItem
            status={rebateProgram.status === 'claimed' ? 'complete' : 'pending'}
            icon="circleBack"
            title={`Claim your rebate rewards`}
            description={`${new BigNumber(convertMicroDenomToDenom(rebateProgram.amount))
              .decimalPlaces(2)
              .toString()} ${STRIDE_CURRENCY.coinDenom}`}
            action={<AirdropTaskClaimRebateButton />}
          />
        )}

        {Boolean(totalClaimable.coins.length) && (
          <>
            <AirdropTaskItem
              status={taskStatus.claim ? 'complete' : 'pending'}
              icon="claim"
              title={`Claim your free ${STRIDE_CURRENCY.coinDenom} tokens`}
              description={`${taskRewards.claim} ${STRIDE_CURRENCY.coinDenom}`}
              action={<AirdropTaskClaimButton />}
            />

            <AirdropTaskItem
              status={taskStatus.stake ? 'complete' : 'pending'}
              icon="stake"
              title={`Stake some ${STRIDE_CURRENCY.coinDenom}`}
              description={`${taskRewards.stake} ${STRIDE_CURRENCY.coinDenom}`}
              action={
                <AirdropTaskExternalLinkButton
                  url={wallet?.walletInfo?.stakingUrl ?? '/'}
                  description="Stake the tokens you just claimed to get the next 20% of your airdrop. By staking to a Stride validator,
            the next 20% of your airdrop will be automatically sent to your wallet."
                  isLoading={isLoading}>
                  Stake
                </AirdropTaskExternalLinkButton>
              }
            />

            <AirdropTaskItem
              status={taskStatus.liquidStake ? 'complete' : 'pending'}
              icon="arrowHorizontal"
              title="Liquid Stake on Stride"
              description={`${taskRewards.liquidStake} ${STRIDE_CURRENCY.coinDenom}`}
              action={
                <AirdropTaskExternalLinkButton
                  url="/"
                  description="Liquid stake any amount of any token to get the remainder of your airdrop.">
                  Liquid Stake
                </AirdropTaskExternalLinkButton>
              }
            />
          </>
        )}
      </Stack>
    </>
  )
}

export { AirdropTasks }
